<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  totalContacts: { type: Number, default: 0 },
  appliedFilters: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
  'clearFilters',
  'removeFilter',
]);

const { t } = useI18n();

// Format filter for display
const formatFilterValue = filter => {
  if (!filter) return '';

  const key = filter.attributeKey || filter.attribute_key || '';
  const operator = filter.filterOperator || filter.filter_operator || '';
  let value = filter.values;

  // Handle array values (like [{ id: 'VIP2', name: 'VIP2' }] or ['VIP2'])
  if (Array.isArray(value)) {
    value = value
      .map(v => {
        if (v && typeof v === 'object') return v.name || v.id;
        return v;
      })
      .join(', ');
  } else if (value && typeof value === 'object') {
    // Handle single object value
    value = value.name || value.id || JSON.stringify(value);
  }

  // Format operator
  const operatorMap = {
    equal_to: 'is',
    not_equal_to: 'is not',
    contains: 'contains',
    does_not_contain: 'does not contain',
    is_present: 'is present',
    is_not_present: 'is not present',
    is_greater_than: '>',
    is_less_than: '<',
    days_before: 'days before',
  };

  const formattedOperator = operatorMap[operator] || operator;

  // Format key
  const keyMap = {
    name: 'Name',
    email: 'Email',
    phone_number: 'Phone',
    vip: 'VIP',
    issue_id: 'Issue',
    last_activity_at: 'Last seen',
    created_at: 'Created',
    country_code: 'Country',
    city: 'City',
    blocked: 'Blocked',
    labels: 'Labels',
  };

  const formattedKey = keyMap[key] || key;

  return { key: formattedKey, operator: formattedOperator, value };
};

const displayFilters = computed(() => {
  if (!props.appliedFilters || props.appliedFilters.length === 0) return [];
  return props.appliedFilters.map((filter, index) => ({
    ...formatFilterValue(filter),
    index,
    raw: filter,
  }));
});
</script>

<template>
  <header class="contact-header">
    <div class="header-bg">
      <div class="header-glow" />
      <div class="header-pattern" />
    </div>

    <div class="header-inner">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-3 min-w-0">
            <div class="header-icon">
              <span class="i-ph-users-three-bold size-5" />
            </div>
            <div class="min-w-0">
              <h1 class="header-title">
                {{ headerTitle }}
              </h1>
              <p class="header-meta">
                {{ totalContacts }}
                {{ t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS') }}
              </p>
            </div>
          </div>

          <div
            v-if="displayFilters.length > 0"
            class="flex flex-wrap items-center gap-2 mt-3"
          >
            <div
              v-for="filter in displayFilters"
              :key="filter.index"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-n-alpha-2 text-sm text-n-slate-12"
            >
              <span class="i-ph-funnel-bold size-4 text-n-slate-11" />
              <span class="truncate max-w-[22rem]">
                {{ filter.key }} {{ filter.operator }} {{ filter.value }}
              </span>
              <button
                type="button"
                class="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-n-alpha-3"
                :aria-label="t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS')"
                @click.stop="emit('removeFilter', filter.index)"
              >
                <span class="i-ph-x-bold size-3 text-n-slate-11" />
              </button>
            </div>

            <Button
              variant="link"
              color="slate"
              size="sm"
              class="hover:no-underline"
              :label="t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS')"
              @click="emit('clearFilters')"
            />
          </div>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0">
          <ComposeConversation>
            <template #trigger="{ toggle }">
              <Button
                :label="t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
                icon="i-ph-paper-plane-tilt-bold"
                color="blue"
                size="sm"
                class="tm__btn tm__btn--primary"
                @click="toggle"
              />
            </template>
          </ComposeConversation>

          <ContactMoreActions
            @add="emit('add')"
            @import="emit('import')"
            @export="emit('export')"
          />
        </div>
      </div>

      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5"
      >
        <div class="flex items-center flex-wrap gap-2">
          <div class="relative">
            <Button
              id="toggleContactsFilterButton"
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-ph-funnel-bold"
              :label="t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER')"
              @click="emit('filter')"
            />
            <slot name="filter" />
          </div>
        </div>

        <div class="flex items-center gap-2 w-full sm:w-auto">
          <ContactSortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />

          <div v-if="showSearch" class="relative w-full sm:w-72">
            <span
              class="contact-search-icon absolute i-ph-magnifying-glass-bold size-4 top-1/2 -translate-y-1/2 left-3 text-n-slate-11 pointer-events-none"
            />
            <input
              type="search"
              :value="searchValue"
              class="contact-search-input w-full h-10 pl-10 pr-3 text-sm outline-none border border-n-weak rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-11"
              :placeholder="t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
              @input="emit('search', $event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.contact-header {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.header-glow {
  position: absolute;
  top: -70%;
  right: -10%;
  width: 420px;
  height: 420px;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.08) 0%,
    transparent 70%
  );
}

.header-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(15, 23, 42, 0.04) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
  opacity: 0.6;
}

.header-inner {
  position: relative;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.14) 0%,
    rgba(139, 92, 246, 0.08) 100%
  );
  border: 1px solid rgba(99, 102, 241, 0.18);
  color: #4f46e5;
  flex-shrink: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-meta {
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
}

.contact-search-input {
  padding-left: 2.75rem !important;
}

.contact-search-icon {
  left: 0.875rem;
  top: 35% !important;
}
</style>
